import { useTranslation } from "react-i18next";
import { Text } from "react-native-paper";
import { useAppTheme } from "../../useAppTheme";
import { useDevice } from "../../useDevice";
import Box from "../Box";
import Button from "../Button";
import { FlatCard } from "../Cards/FlatCard";
import { LessonDetailMetaChips } from "../LessonDetailViewScreen/LessonDetailMetaChips";
import { LessonExploreThumbnail } from "./LessonExploreThumbnail";
import { getLessonActionConfig, getLessonProgressStatus } from "./helpers";
import type { ExploreLesson } from "./types";

type Props = {
	lesson: ExploreLesson;
	layout: "row" | "stacked";
	highlightInProgress?: boolean;
	onPress: (lesson: ExploreLesson) => void;
};

export const LessonExploreCard = ({
	lesson,
	layout,
	highlightInProgress = false,
	onPress,
}: Props) => {
	const theme = useAppTheme();
	const { isMobile } = useDevice();
	const { t } = useTranslation();
	const status = getLessonProgressStatus(lesson.progressPercent);
	const isHighlighted = highlightInProgress && status === "inProgress";
	const action = getLessonActionConfig(status);

	const chips = [
		{ label: lesson.meta.specialty },
		{
			label: t("lessons.explore.card.duration", {
				count: lesson.meta.durationMinutes,
			}),
		},
	];

	const actionButton = (
		<Button
			mode={action.mode}
			icon={action.iconName}
			compact={action.mode === "text"}
			onPress={() => onPress(lesson)}
		>
			{t(action.labelKey)}
		</Button>
	);

	return (
		<FlatCard
			onPress={() => onPress(lesson)}
			style={{
				borderRadius: isMobile ? 0 : theme.roundness * 3,
				...(isHighlighted
					? { borderWidth: 1, borderColor: theme.colors.primary }
					: null),
			}}
		>
			{layout === "row" ? (
				<Box flexDirection="row" alignItems="flex-start" gap={16} padding={16}>
					<LessonExploreThumbnail
						thumbnailUrl={lesson.thumbnailUrl}
						status={status}
						progressPercent={lesson.progressPercent}
					/>
					<Box flex={1} gap={8}>
						<Box gap={4}>
							<Text variant="titleMedium">{lesson.title}</Text>
							<Text
								variant="bodyMedium"
								numberOfLines={1}
								style={{ color: theme.colors.onSurfaceVariant }}
							>
								{lesson.description}
							</Text>
						</Box>
						<LessonDetailMetaChips chips={chips} />
					</Box>
					<Box alignSelf="center">{actionButton}</Box>
				</Box>
			) : (
				<Box gap={16} padding={16}>
					<Box flexDirection="row" alignItems="flex-start" gap={16}>
						<LessonExploreThumbnail
							thumbnailUrl={lesson.thumbnailUrl}
							status={status}
							progressPercent={lesson.progressPercent}
						/>
						<Box flex={1} gap={4}>
							<Text variant="titleMedium">{lesson.title}</Text>
							<Text
								variant="bodyMedium"
								numberOfLines={2}
								style={{ color: theme.colors.onSurfaceVariant }}
							>
								{lesson.description}
							</Text>
						</Box>
					</Box>
					<Box
						flexDirection="row"
						alignItems="center"
						justifyContent="space-between"
						gap={8}
					>
						<LessonDetailMetaChips chips={chips} />
						{actionButton}
					</Box>
				</Box>
			)}
		</FlatCard>
	);
};
